import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, CircularProgress, TextField, Tooltip, IconButton, Alert, Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SavedIcon,
  ErrorOutline as ErrorIcon,
  QuestionAnswer as QuestionsIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../../config/api';
import useStore from '../../config/store';

// Retardo del autoguardado (mismo criterio que las respuestas de los tests
// públicos): se guarda solo cuando el reclutador deja de escribir.
const AUTOSAVE_DELAY_MS = 2000;

// Los Timestamp de Firestore llegan serializados como { _seconds } (o { seconds }).
const formatDate = (ts) => {
  if (!ts) return '';
  const seconds = ts._seconds ?? ts.seconds;
  const date = seconds != null ? new Date(seconds * 1000) : new Date(ts);
  return Number.isNaN(date.getTime()) ? '' : dayjs(date).format('DD/MM/YYYY');
};

const authorLine = (question) =>
  [question.updatedByName, formatDate(question.updatedAt)].filter(Boolean).join(' · ');

/**
 * Modal de respuestas de entrevista en la ficha del candidato (drawer).
 *
 * Muestra las preguntas del CARGO (texto fijo: se definen en el modal de
 * entrevistas) con la respuesta de este candidato. Las respuestas son
 * compartidas por el equipo de la vacante: cada una muestra quién la escribió
 * y cuándo. Autoguardado por pregunta: solo se envían las editadas, así dos
 * reclutadores no se pisan.
 *
 * Al final, en solo lectura, las respuestas a preguntas que se eliminaron del
 * cargo (con el texto que tenía la pregunta al responder).
 *
 * El estado vive aquí y se propaga al drawer vía onChange para que su botón de
 * entrada refleje el avance sin volver a pedirlo al backend.
 */
const AtsInterviewQuestionsDialog = ({
  open,
  onClose,
  applicationId,
  candidateName = '',
  questions: initialQuestions = [],
  removedQuestions = [],
  canEdit = true,
  onChange,
}) => {
  const { t } = useTranslation();
  const { authToken, showAlert } = useStore.getState();

  const [questions, setQuestions] = useState(initialQuestions);
  const [saveState, setSaveState] = useState(null); // null | 'saving' | 'saved' | 'error'

  // Estado local más reciente + ids editados desde el último guardado (para
  // vaciar lo pendiente al cerrar el modal y no perder lo último escrito).
  const questionsRef = useRef(initialQuestions);
  const dirtyRef = useRef(new Set());
  const saveTimerRef = useRef(null);
  const savedTimerRef = useRef(null);
  // El padre puede pasar callbacks nuevos en cada render: se leen por ref para
  // no reiniciar el autoguardado.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const persist = useCallback(async () => {
    const ids = [...dirtyRef.current];
    if (ids.length === 0) return;
    dirtyRef.current.clear();
    const toSave = questionsRef.current.filter((q) => ids.includes(q.id));

    setSaveState('saving');
    try {
      const { data } = await api.put(
        `/applications/${applicationId}/interview-questions`,
        { questions: toSave.map((q) => ({ id: q.id, answer: q.answer || '' })) },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      // Autor y fecha de lo guardado. El texto local no se toca: el reclutador
      // puede seguir escribiendo mientras llega la respuesta.
      const answers = data.response?.data || {};
      const next = questionsRef.current.map((q) => {
        if (!ids.includes(q.id)) return q;
        const entry = answers[q.id];
        return {
          ...q,
          updatedByName: entry?.updatedByName || null,
          updatedAt: entry?.updatedAt || null,
        };
      });
      questionsRef.current = next;
      setQuestions(next);
      if (onChangeRef.current) onChangeRef.current(next);

      setSaveState('saved');
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveState(null), 2000);
    } catch (err) {
      console.error('Error guardando respuestas de entrevista:', err);
      // Quedan pendientes para el próximo guardado (o al cerrar). El estado de
      // error queda visible (no se autolimpia) para que no se confunda con
      // guardado.
      ids.forEach((id) => dirtyRef.current.add(id));
      setSaveState('error');
      showAlert(t('ats.interviewQuestions.saveError'), 'error');
    }
  }, [applicationId, authToken, showAlert, t]);

  const persistRef = useRef(persist);
  persistRef.current = persist;

  // Al desmontar: guardar lo que quedó pendiente.
  useEffect(() => () => {
    clearTimeout(savedTimerRef.current);
    clearTimeout(saveTimerRef.current);
    if (dirtyRef.current.size > 0) persistRef.current();
  }, []);

  const handleAnswerChange = (questionId, value) => {
    const next = questionsRef.current.map((q) => (q.id === questionId ? { ...q, answer: value } : q));
    questionsRef.current = next;
    setQuestions(next);
    if (onChangeRef.current) onChangeRef.current(next);
    dirtyRef.current.add(questionId);
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      persistRef.current();
    }, AUTOSAVE_DELAY_MS);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QuestionsIcon sx={{ color: '#7C3AED' }} />
            <Typography variant="h6" fontWeight={700}>
              {t('ats.interviewQuestions.title')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {saveState === 'saving' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={12} />
                <Typography variant="caption" color="text.disabled">
                  {t('ats.interviewQuestions.saving')}
                </Typography>
              </Box>
            )}
            {saveState === 'saved' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <SavedIcon sx={{ fontSize: 14, color: '#10B981' }} />
                <Typography variant="caption" color="text.disabled">
                  {t('ats.interviewQuestions.saved')}
                </Typography>
              </Box>
            )}
            {saveState === 'error' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ErrorIcon sx={{ fontSize: 14, color: '#EF4444' }} />
                <Typography variant="caption" sx={{ color: '#EF4444' }}>
                  {t('ats.interviewQuestions.notSaved')}
                </Typography>
              </Box>
            )}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        {candidateName && (
          <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
            {candidateName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
          <Typography variant="caption">
            {t('ats.interviewQuestions.sharedHint')}
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {questions.map((question, index) => {
            const author = authorLine(question);
            return (
              <Box key={question.id}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      backgroundColor: '#6A5AF9',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ flexGrow: 1, whiteSpace: 'pre-line', pt: '1px' }}
                  >
                    {question.text}
                  </Typography>
                  {question.rationale && (
                    <Tooltip
                      title={t('ats.interviewQuestions.rationale', { text: question.rationale })}
                      placement="top"
                    >
                      <InfoIcon sx={{ fontSize: 16, color: 'text.disabled', mt: '2px', flexShrink: 0 }} />
                    </Tooltip>
                  )}
                </Box>

                {canEdit ? (
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    placeholder={t('ats.interviewQuestions.answerPlaceholder')}
                    value={question.answer || ''}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    inputProps={{ maxLength: 5000 }}
                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#FAFAFA' } }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    color={question.answer ? 'text.primary' : 'text.disabled'}
                    sx={{ whiteSpace: 'pre-line', pl: 3.75 }}
                  >
                    {question.answer || t('ats.interviewQuestions.noAnswer')}
                  </Typography>
                )}

                {author && (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}
                  >
                    {author}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Respuestas a preguntas eliminadas del cargo: solo lectura */}
        {removedQuestions.length > 0 && (
          <Box sx={{ mt: questions.length > 0 ? 3 : 0 }}>
            {questions.length > 0 && <Divider sx={{ mb: 2 }} />}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ display: 'block', mb: 1.5 }}
            >
              {t('ats.interviewQuestions.removedTitle')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {removedQuestions.map((question) => {
                const author = authorLine(question);
                return (
                  <Box key={question.id}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ whiteSpace: 'pre-line', mb: 0.5 }}
                    >
                      {question.text}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {question.answer}
                    </Typography>
                    {author && (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}
                      >
                        {author}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsInterviewQuestionsDialog;
